import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { getAccessToken } from "../../../utils/tokenManager";
import { getAuthToken } from "../../../utils/getAuthToken";

const API_URL = `${process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000'}/api/workspaces`;

// Last-opened workspace, remembered independently of the "user" localStorage entry.
// authSlice's login reducers intentionally overwrite "user" with just {name} on every
// fresh login (XSS hygiene — see loginUser.fulfilled), which was wiping out the
// workspaceId switchWorkspace had written there and bouncing users back to the
// account's default workspace on next login. This key survives that reset.
const LAST_WORKSPACE_STORAGE_KEY = "lastWorkspaceId";

const getLastWorkspaceId = () => {
  try {
    return localStorage.getItem(LAST_WORKSPACE_STORAGE_KEY) || null;
  } catch {
    return null;
  }
};

const setLastWorkspaceId = (workspaceId) => {
  try {
    if (workspaceId) {
      localStorage.setItem(LAST_WORKSPACE_STORAGE_KEY, workspaceId);
    }
  } catch {
    // localStorage unavailable (private mode, quota, etc.) — non-fatal, just skip persistence.
  }
};

const wsDebounceMap = new Map();
const WS_DEBOUNCE_TIME = 5000;
const shouldDebounceWS = () => {
  const now = Date.now();
  const last = wsDebounceMap.get('fetchWorkspaces');
  if (last && (now - last) < WS_DEBOUNCE_TIME) return true;
  wsDebounceMap.set('fetchWorkspaces', now);
  return false;
};

// Clears the module-level debounce map/cache — call on logout so the next
// account's first fetch is never silently skipped or served stale data.
export const __resetWorkspaceCaches = () => {
  wsDebounceMap.clear();
  cachedWorkspaces = null;
  cachedAt = 0;
};

// Shared GET /api/workspaces call. Two layers of sharing:
//  - in-flight dedupe: concurrent callers (e.g. authSlice's validateToken and
//    the fetchWorkspaces thunk racing on the same page load) get the same
//    promise instead of firing two requests.
//  - short TTL cache: callers that land a moment *after* a previous call
//    already resolved (the common case — these calls are rarely truly
//    concurrent) get the just-fetched list instead of triggering a new
//    request. Invalidated immediately on any create/update/delete.
const WS_CACHE_TTL = 5000;
let cachedWorkspaces = null;
let cachedAt = 0;
let inFlightWorkspacesRequest = null;

export function requestWorkspacesList(token) {
  const now = Date.now();
  if (cachedWorkspaces && (now - cachedAt) < WS_CACHE_TTL) {
    return Promise.resolve(cachedWorkspaces);
  }
  if (inFlightWorkspacesRequest) return inFlightWorkspacesRequest;
  inFlightWorkspacesRequest = axios
    .get(API_URL, { headers: { Authorization: `Bearer ${token}` } })
    .then((response) => {
      const workspaces = response.data?.workspaces || [];
      cachedWorkspaces = workspaces;
      cachedAt = Date.now();
      return workspaces;
    })
    .finally(() => {
      inFlightWorkspacesRequest = null;
    });
  return inFlightWorkspacesRequest;
}

// Fetch all workspaces
export const fetchWorkspaces = createAsyncThunk(
  "workspace/fetchWorkspaces",
  async (_, { getState, dispatch, rejectWithValue }) => {
    if (shouldDebounceWS()) return rejectWithValue("Debounced");

    try {
      let token = getAccessToken();
      if (!token) {
        token = await getAuthToken();
      }
      if (!token) {
        return rejectWithValue("No authentication token available");
      }
      const workspaces = await requestWorkspacesList(token);

      // Every fresh login issues an access token scoped to the account's
      // default workspace (server/services/auth/login.service.js), regardless
      // of which workspace was active before logging out. The switcher's
      // fulfilled reducer below picks lastWorkspaceId for *display*, but
      // without this the token stays scoped to the default workspace, so the
      // switcher shows the remembered workspace while every API call keeps
      // returning the default workspace's data. Reissue the session so the
      // token actually matches what's displayed.
      const lastWorkspaceId = getLastWorkspaceId();
      const sessionWorkspaceId = getState().auth.user?.workspaceId;
      if (
        lastWorkspaceId &&
        sessionWorkspaceId &&
        lastWorkspaceId !== sessionWorkspaceId &&
        workspaces.some((w) => w._id === lastWorkspaceId)
      ) {
        try {
          await dispatch(switchWorkspace(lastWorkspaceId)).unwrap();
          // The switch reissues the access token, but any screen that already
          // mounted and fetched data (with the old, default-workspace token)
          // won't re-fetch on its own - reload so everything picks up the
          // corrected workspace, same as the manual-switch flow in
          // WorkspaceSwitcher.handleSwitchWorkspace. This also refreshes
          // auth.user.workspaceId, so this correction won't re-fire.
          if (typeof window !== "undefined") {
            window.location.reload();
          }
        } catch {
          // Switch failed (e.g. no longer a member of that workspace) - keep
          // whatever workspace the current token is actually scoped to.
        }
      }

      return workspaces;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

// Create workspace
export const createWorkspace = createAsyncThunk(
  "workspace/createWorkspace",
  async (workspaceData, { rejectWithValue }) => {
    try {
      let token = getAccessToken();
      if (!token) {
        token = await getAuthToken();
      }
      if (!token) {
        return rejectWithValue("No authentication token available");
      }
      const response = await axios.post(API_URL, workspaceData, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      cachedWorkspaces = null;
      return response.data.workspace;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

// Update workspace
export const updateWorkspace = createAsyncThunk(
  "workspace/updateWorkspace",
  async ({ workspaceId, workspaceData }, { rejectWithValue }) => {
    try {
      let token = getAccessToken();
      if (!token) {
        token = await getAuthToken();
      }
      if (!token) {
        return rejectWithValue("No authentication token available");
      }
      const response = await axios.put(
        `${API_URL}/${workspaceId}`,
        workspaceData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      cachedWorkspaces = null;
      return response.data.workspace;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

// Delete workspace
export const deleteWorkspace = createAsyncThunk(
  "workspace/deleteWorkspace",
  async (workspaceId, { rejectWithValue }) => {
    try {
      let token = getAccessToken();
      if (!token) {
        token = await getAuthToken();
      }
      if (!token) {
        return rejectWithValue("No authentication token available");
      }
      await axios.delete(`${API_URL}/${workspaceId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      cachedWorkspaces = null;
      return workspaceId;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

// Switch workspace
export const switchWorkspace = createAsyncThunk(
  "workspace/switchWorkspace",
  async (workspaceId, { rejectWithValue }) => {
    try {
      // Get token from tokenManager (memory) or refresh if needed
      let token = getAccessToken();
      if (!token) {
        token = await getAuthToken();
      }
      
      if (!token) {
        return rejectWithValue("No authentication token available");
      }
      
      console.log('🔄 Switching workspace to:', workspaceId);
      
      const response = await axios.post(
        `${API_URL}/switch`,
        { workspaceId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      console.log('✅ Workspace switch response:', response.data);
      
      // Update token in localStorage
      const user = localStorage.getItem("user");
      if (user) {
        const parsedUser = JSON.parse(user);
        parsedUser.token = response.data.accessToken;
        parsedUser.accessToken = response.data.accessToken;
        parsedUser.workspaceId = response.data.workspaceId;
        localStorage.setItem("user", JSON.stringify(parsedUser));
      }
      setLastWorkspaceId(response.data.workspaceId);

      // CRITICAL: Update tokenManager (memory) with new token
      // This prevents logout after page reload
      const { setAccessToken } = require('../../../utils/tokenManager');
      const expiresIn = 30 * 24 * 60 * 60; // 30 days in seconds (matching server token expiry)
      setAccessToken(response.data.accessToken, expiresIn);
      
      console.log('✅ Token updated in both localStorage and tokenManager');
      
      return response.data;
    } catch (error) {
      console.error('❌ Error switching workspace:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      return rejectWithValue(
        error.response?.data?.message || error.message || "Failed to switch workspace"
      );
    }
  }
);

const initialState = {
  workspaces: [],
  currentWorkspace: null,
  loading: false,
  error: null,
  isOwner: false, // Will be set based on user permissions
};

const workspaceSlice = createSlice({
  name: "workspace",
  initialState,
  reducers: {
    setCurrentWorkspace: (state, action) => {
      state.currentWorkspace = action.payload;
      if (action.payload?._id) {
        setLastWorkspaceId(action.payload._id);
      }
    },
    clearWorkspaces: (state) => {
      state.workspaces = [];
      state.currentWorkspace = null;
    },
    setOwnerStatus: (state, action) => {
      state.isOwner = action.payload;
    },
    // Live QR (Baileys) connection-state patch (2026-09-26 follow-up) -- currentWorkspace's own
    // whatsappQrState is otherwise only a snapshot from whichever HTTP fetch last loaded this
    // workspace (workspaceController.js's attachBaileysStatus), so a disconnect/logout happening
    // while the Inbox is already open would never be reflected without a manual refresh. Fed by
    // the existing `whatsapp_qr:status` socket event (already emitted on every state transition,
    // already listened to elsewhere for the QR-scan flow) -- see InboxAll.jsx's own listener.
    // Deliberately does NOT touch whatsappConnectionType (the identity field Template/Catalog
    // gating reads) -- that field's own "stays baileys through everything but LOGGED_OUT" design
    // is unrelated to this live health signal and must not be recomputed client-side.
    updateWhatsappQrState: (state, action) => {
      if (state.currentWorkspace) {
        state.currentWorkspace.whatsappQrState = action.payload;
      }
    },
    // Multi-number (2026-10): live state of ONE additional number (fed by the same
    // `whatsapp_qr:status` event, which carries connectionId/slot). Patches the per-number list the
    // numbers UI/filter read; the single-number fields above keep tracking the first number only.
    updateWhatsappQrConnection: (state, action) => {
      const p = action.payload;
      const list = state.currentWorkspace?.whatsappQrConnections;
      if (!p?.connectionId || !Array.isArray(list)) return;
      const entry = list.find((c) => c.connectionId === p.connectionId);
      if (entry) {
        if (p.state) entry.state = p.state;
        if (p.phoneNumber) entry.phoneNumber = p.phoneNumber;
        if (p.label) entry.label = p.label;
      }
    },
    // Live send-limit state of ONE number (burst rest / daily cap), keyed by connectionId, so a chat's composer follows
    // the number that chat belongs to. `burstDraining` = the limit was hit and the accepted messages are still going
    // out (the rest has not started yet).
    updateWhatsappQrConnectionLimits: (state, action) => {
      const p = action.payload;
      const list = state.currentWorkspace?.whatsappQrConnections;
      if (!p?.connectionId || !Array.isArray(list)) return;
      const entry = list.find((c) => c.connectionId === p.connectionId);
      if (!entry) return;
      if ("burstPauseUntil" in p) entry.burstPauseUntil = p.burstPauseUntil ?? null;
      if ("burstDraining" in p) entry.burstDraining = Boolean(p.burstDraining);
      if ("dailyLimitReached" in p) entry.dailyLimitReached = Boolean(p.dailyLimitReached);
    },
    // Composer-hiding signals for QR (Baileys) send-limit conditions (2026-09-28 follow-up to
    // the isQrDisconnected work above) -- fed by the existing whatsapp_qr:burst_pause/
    // daily_limit_alert socket events (already listened to for their own toasts in InboxAll.jsx,
    // just not previously fed into currentWorkspace) AND by workspaceController.js's
    // attachBaileysStatus on every workspace fetch (a later follow-up the same day closed the
    // reload gap this comment used to describe -- both a REST snapshot on load and these live
    // updates during the session now agree on the same field names).
    updateWhatsappQrBurstPause: (state, action) => {
      if (state.currentWorkspace) {
        state.currentWorkspace.whatsappQrBurstPauseUntil = action.payload ?? null;
      }
    },
    // Payload is {reached, resetAt} (2026-09-28 follow-up: resetAt added so TextBox.jsx can show
    // when the cap actually clears, always the next Karachi midnight by construction).
    updateWhatsappQrDailyLimitReached: (state, action) => {
      if (state.currentWorkspace) {
        state.currentWorkspace.whatsappQrDailyLimitReached = action.payload?.reached === true;
        state.currentWorkspace.whatsappQrDailyLimitResetAt = action.payload?.resetAt ?? null;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch workspaces
      .addCase(fetchWorkspaces.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchWorkspaces.fulfilled, (state, action) => {
        state.loading = false;
        state.workspaces = action.payload;
        if (state.currentWorkspace) {
          // Refresh with the latest fetched copy (e.g. whatsappConnected/
          // instagramConnected/facebookConnected flipping after a channel
          // connects) instead of leaving the stale pre-fetch object in place.
          const refreshed = action.payload.find((w) => w._id === state.currentWorkspace._id);
          if (refreshed) state.currentWorkspace = refreshed;
          return;
        }
        // Not set yet. Prefer the workspace the user was actually last switched
        // to (persisted by switchWorkspace/setCurrentWorkspace, independently of
        // the "user" entry login clears) over the account's default, otherwise a
        // fresh login/page load always bounces the user back to the default
        // workspace before it can be corrected.
        if (action.payload.length > 0) {
          let selectedWorkspace = null;
          const lastWorkspaceId = getLastWorkspaceId();
          if (lastWorkspaceId) {
            selectedWorkspace = action.payload.find((w) => w._id === lastWorkspaceId) || null;
          }
          if (!selectedWorkspace) {
            // Fall back to the older "user.workspaceId" location for anyone whose
            // browser only has that value from before this key existed.
            try {
              const storedUser = JSON.parse(localStorage.getItem("user") || "null");
              if (storedUser?.workspaceId) {
                selectedWorkspace = action.payload.find((w) => w._id === storedUser.workspaceId) || null;
              }
            } catch {
              selectedWorkspace = null;
            }
          }
          state.currentWorkspace =
            selectedWorkspace || action.payload.find((w) => w.isDefault) || action.payload[0];
        }
      })
      .addCase(fetchWorkspaces.rejected, (state, action) => {
        state.loading = false;
        if (action.payload === "Debounced") return;
        state.error = action.payload;
      })
      // Create workspace
      .addCase(createWorkspace.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createWorkspace.fulfilled, (state, action) => {
        state.loading = false;
        state.workspaces.push(action.payload);
      })
      .addCase(createWorkspace.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update workspace
      .addCase(updateWorkspace.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateWorkspace.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.workspaces.findIndex(
          (w) => w._id === action.payload._id
        );
        if (index !== -1) {
          state.workspaces[index] = action.payload;
        }
        if (state.currentWorkspace?._id === action.payload._id) {
          state.currentWorkspace = action.payload;
        }
      })
      .addCase(updateWorkspace.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete workspace
      .addCase(deleteWorkspace.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteWorkspace.fulfilled, (state, action) => {
        state.loading = false;
        state.workspaces = state.workspaces.filter(
          (w) => w._id !== action.payload
        );
        if (state.currentWorkspace?._id === action.payload) {
          state.currentWorkspace = state.workspaces[0] || null;
        }
      })
      .addCase(deleteWorkspace.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Switch workspace
      .addCase(switchWorkspace.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(switchWorkspace.fulfilled, (state, action) => {
        state.loading = false;
        state.currentWorkspace = action.payload.workspace;
      })
      .addCase(switchWorkspace.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { setCurrentWorkspace, clearWorkspaces, setOwnerStatus, updateWhatsappQrState, updateWhatsappQrConnection, updateWhatsappQrConnectionLimits, updateWhatsappQrBurstPause, updateWhatsappQrDailyLimitReached } =
  workspaceSlice.actions;
export default workspaceSlice.reducer;
